// Whether a server's player count is a statement or a guess.
//
// This is what replaced the ghost-slot readout. Ghost slots asked staff to hold
// a mechanism in their head — a join reserves a slot before the player lands,
// only a roster sync releases it, so a number of the slots you can see are not
// people — and then to decide whether to press a button about it. The mechanism
// is fixed: a server that reports rewrites its whole roster every ~30s, so the
// count is current (as the server states it) and corrects itself, and a server that does not report no
// longer refuses anybody.
//
// So the only thing left worth surfacing is which of those two a server is, and
// it lives in one component because the fleet tab, the card header and the
// health panel all answer it and must not answer it differently.

import { faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export const agoLabel = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return 'never'
    if (seconds < 60) return `${Math.max(0, Math.round(seconds))}s ago`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`
    return `${Math.round(seconds / 3600)}h ago`
}

const NOT_REPORTING_TITLE =
    'This server is not stating its roster on a timer, so its player count is an ' +
    'estimate built from join reservations. Nothing is being refused because of it — ' +
    'capacity is only enforced against a roster we know is complete. A game build ' +
    'carrying the population reporter fixes it.'

// "As reported", not "exact" (decision D-072): every game server signs its
// report with the same fleet-wide key, so the backend cannot tell which server
// sent it. The count is the server's own statement, current but unverified.
const REPORTING_TITLE =
    'This server restates who is on it every ~30s, and each report replaces ' +
    'the last, so the count does not drift. It is what the server reports: the ' +
    'game servers share one key, so the report is not verified against the server.'

/** Inline, for a tight row: a dot and nothing else unless something is wrong. */
export const ReportingDot = ({
    reporting,
    ageSeconds,
}: {
    reporting: boolean
    ageSeconds?: number | null
}) =>
    reporting ? (
        <span
            className='shrink-0 text-[10px] text-emerald-400/70'
            title={`${REPORTING_TITLE} Last report ${agoLabel(ageSeconds)}.`}
        >
            <FontAwesomeIcon icon={faCircleCheck} />
        </span>
    ) : (
        <span
            className='flex shrink-0 flex-row items-center gap-1 text-[10px] uppercase tracking-wide text-amber-400'
            title={NOT_REPORTING_TITLE}
        >
            <FontAwesomeIcon icon={faTriangleExclamation} />
            estimated
        </span>
    )

/** Block form, for the health panel where there is room to explain. */
export const ReportingRow = ({
    reporting,
    ageSeconds,
}: {
    reporting: boolean
    ageSeconds?: number | null
}) => (
    <div
        className={`flex flex-row items-center gap-2 text-xs ${
            reporting ? 'opacity-60' : 'text-amber-400'
        }`}
        title={reporting ? REPORTING_TITLE : NOT_REPORTING_TITLE}
    >
        <FontAwesomeIcon icon={reporting ? faCircleCheck : faTriangleExclamation} />
        {reporting ? (
            <span>Roster reported {agoLabel(ageSeconds)}, count as reported by the server</span>
        ) : (
            <span>Not reporting its roster — count is an estimate, capacity not enforced</span>
        )}
    </div>
)
